import React, { useCallback, useEffect, useMemo, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Clock, FolderKanban, Table } from "lucide-react"
import { appConfirm } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })
  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null
  if (!res.ok) {
    const message = data?.error ? `${data.error}` : `Request failed (${res.status})`
    throw new Error(message)
  }
  return data
}

function formatDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
}

function TextInput({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      />
    </label>
  )
}

function SelectInput({ label, value, onChange, options }) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function ProjectsPanel() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ name: "", code: "", status: "active", notes: "" })

  const load = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const data = await fetchJson("/api/time_tracking/projects")
      setProjects(data || [])
    } catch (e) {
      setError(e.message || "Load failed")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function onSubmit(e) {
    e.preventDefault()
    try {
      setError("")
      const body = JSON.stringify({
        time_project: {
          name: form.name.trim(),
          code: form.code.trim() || null,
          status: form.status,
          notes: form.notes.trim() || null,
        },
      })
      if (editingId) {
        await fetchJson(`/api/time_tracking/projects/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson("/api/time_tracking/projects", { method: "POST", body })
      }
      await load()
      setEditingId(null)
      setForm({ name: "", code: "", status: "active", notes: "" })
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  async function onDelete(id) {
    if (!(await appConfirm("Delete this project and its time entries?", { title: "Delete project", danger: true, confirmLabel: "Delete" }))) return
    try {
      await fetchJson(`/api/time_tracking/projects/${id}`, { method: "DELETE" })
      await load()
      if (editingId === id) {
        setEditingId(null)
        setForm({ name: "", code: "", status: "active", notes: "" })
      }
    } catch (e) {
      setError(e.message || "Delete failed")
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{editingId ? "Edit project" : "New project"}</CardTitle>
          {editingId ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEditingId(null)
                setForm({ name: "", code: "", status: "active", notes: "" })
              }}
            >
              Cancel
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
          <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
            <TextInput label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} placeholder="Vessel refit" />
            <TextInput label="Code" value={form.code} onChange={(v) => setForm((f) => ({ ...f, code: v }))} placeholder="PRJ-001" />
            <SelectInput
              label="Status"
              value={form.status}
              onChange={(v) => setForm((f) => ({ ...f, status: v }))}
              options={[
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
            />
            <div className="md:col-span-2">
              <TextInput label="Notes" value={form.notes} onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit">{editingId ? "Save" : "Create"}</Button>
              <Button type="button" variant="ghost" onClick={() => load()}>
                Refresh
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>Projects</CardTitle>
          <span className="text-sm text-muted-foreground">{loading ? "Loading…" : `${projects.length} total`}</span>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} className="border-b border-border/60">
                    <td className="py-2 pr-4 font-medium text-foreground/85">{p.name}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{p.code || "—"}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{p.status}</td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 px-2"
                          onClick={() => {
                            setEditingId(p.id)
                            setForm({
                              name: p.name || "",
                              code: p.code || "",
                              status: p.status || "active",
                              notes: p.notes || "",
                            })
                          }}
                        >
                          Edit
                        </Button>
                        <Button type="button" variant="ghost" className="h-8 px-2 text-red-600" onClick={() => onDelete(p.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && projects.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-muted-foreground">
                      No projects yet — create one to enable timesheets (Zoho-style ordering).
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function TimesheetsPanel() {
  const [projects, setProjects] = useState([])
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")
  const [projectFilter, setProjectFilter] = useState("")
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ time_project_id: "", worked_on: "", minutes: "60", description: "" })

  const listUrl = useMemo(() => {
    const base = "/api/time_tracking/entries"
    return projectFilter ? `${base}?project_id=${encodeURIComponent(projectFilter)}` : base
  }, [projectFilter])

  const loadAll = useCallback(async () => {
    try {
      setLoading(true)
      setError("")
      const [p, e] = await Promise.all([fetchJson("/api/time_tracking/projects"), fetchJson(listUrl)])
      setProjects(p || [])
      setEntries(e || [])
    } catch (e2) {
      setError(e2.message || "Load failed")
    } finally {
      setLoading(false)
    }
  }, [listUrl])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  async function onSubmit(e) {
    e.preventDefault()
    try {
      setError("")
      const body = JSON.stringify({
        time_entry: {
          time_project_id: Number.parseInt(form.time_project_id, 10),
          worked_on: form.worked_on,
          minutes: Number.parseInt(String(form.minutes || "0"), 10) || 0,
          description: form.description.trim() || null,
        },
      })
      if (editingId) {
        await fetchJson(`/api/time_tracking/entries/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson("/api/time_tracking/entries", { method: "POST", body })
      }
      await loadAll()
      setEditingId(null)
      setForm({ time_project_id: form.time_project_id, worked_on: "", minutes: "60", description: "" })
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  async function onDelete(id) {
    if (!(await appConfirm("Delete this time entry?", { title: "Delete entry", danger: true, confirmLabel: "Delete" }))) return
    try {
      await fetchJson(`/api/time_tracking/entries/${id}`, { method: "DELETE" })
      await loadAll()
      if (editingId === id) setEditingId(null)
    } catch (e) {
      setError(e.message || "Delete failed")
    }
  }

  const projectOptions = projects.map((p) => ({ value: String(p.id), label: p.code ? `${p.name} (${p.code})` : p.name }))

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Log time</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? <div className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
          <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
            <SelectInput
              label="Project"
              value={form.time_project_id}
              onChange={(v) => setForm((f) => ({ ...f, time_project_id: v }))}
              options={projectOptions}
            />
            <TextInput label="Date" value={form.worked_on} onChange={(v) => setForm((f) => ({ ...f, worked_on: v }))} type="date" />
            <TextInput label="Minutes" value={form.minutes} onChange={(v) => setForm((f) => ({ ...f, minutes: v }))} type="number" />
            <div className="md:col-span-2">
              <TextInput label="Description" value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit">{editingId ? "Save entry" : "Add entry"}</Button>
              {editingId ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setEditingId(null)
                    setForm((f) => ({ ...f, worked_on: "", minutes: "60", description: "" }))
                  }}
                >
                  Cancel edit
                </Button>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Time entries</CardTitle>
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Filter project</label>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className="rounded-md border border-border bg-white px-2 py-1 text-sm"
            >
              <option value="">All</option>
              {projectOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-muted-foreground">Phase 1 list — weekly grid and approvals follow Zoho&apos;s phase 2.</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Project</th>
                  <th className="py-2 pr-4">Minutes</th>
                  <th className="py-2 pr-4">Note</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr key={row.id} className="border-b border-border/60">
                    <td className="py-2 pr-4 text-foreground/85">{row.worked_on}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{row.time_project?.name || "—"}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{row.minutes}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{row.description || "—"}</td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          className="h-8 px-2"
                          onClick={() => {
                            setEditingId(row.id)
                            setForm({
                              time_project_id: String(row.time_project_id || row.time_project?.id || ""),
                              worked_on: row.worked_on || "",
                              minutes: String(row.minutes ?? 0),
                              description: row.description || "",
                            })
                          }}
                        >
                          Edit
                        </Button>
                        <Button type="button" variant="ghost" className="h-8 px-2 text-red-600" onClick={() => onDelete(row.id)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && entries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-muted-foreground">
                      No entries — add a project first, then log time here.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function TimerPanel() {
  const [projects, setProjects] = useState([])
  const [activeTimer, setActiveTimer] = useState(null)
  const [elapsed, setElapsed] = useState(0)
  const [selectedProject, setSelectedProject] = useState("")
  const [description, setDescription] = useState("")
  const [loading, setLoading] = useState(true)
  usePageLoading(loading)
  const [error, setError] = useState("")

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const [p, timers] = await Promise.all([
        fetchJson("/api/time_tracking/projects"),
        fetchJson("/api/time_tracking/timers?mine=true&status=running")
      ])
      setProjects(p || [])
      if (timers && timers.length > 0) {
        const t = timers[0]
        setActiveTimer(t)
        setSelectedProject(String(t.time_project_id || ""))
        setDescription(t.description || "")
      }
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!activeTimer || activeTimer.status !== "running") return
    const id = window.setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => window.clearInterval(id)
  }, [activeTimer])

  async function startTimer() {
    if (!selectedProject) return
    try {
      setError("")
      const data = await fetchJson("/api/time_tracking/timers", {
        method: "POST",
        body: JSON.stringify({ time_timer: { time_project_id: Number.parseInt(selectedProject, 10), description } })
      })
      setActiveTimer(data)
      setElapsed(0)
    } catch (e) {
      setError(e.message)
    }
  }

  async function pauseTimer() {
    if (!activeTimer) return
    try {
      setError("")
      await fetchJson(`/api/time_tracking/timers/${activeTimer.id}/pause`, { method: "PATCH" })
      setActiveTimer((prev) => ({ ...prev, status: "paused" }))
    } catch (e) {
      setError(e.message)
    }
  }

  async function resumeTimer() {
    if (!activeTimer) return
    try {
      setError("")
      await fetchJson(`/api/time_tracking/timers/${activeTimer.id}/resume`, { method: "PATCH" })
      setActiveTimer((prev) => ({ ...prev, status: "running" }))
      setElapsed(0)
    } catch (e) {
      setError(e.message)
    }
  }

  async function stopTimer() {
    if (!activeTimer) return
    try {
      setError("")
      await fetchJson(`/api/time_tracking/timers/${activeTimer.id}/stop`, { method: "PATCH" })
      setActiveTimer(null)
      setElapsed(0)
    } catch (e) {
      setError(e.message)
    }
  }

  const projectOptions = projects.map((p) => ({ value: String(p.id), label: p.code ? `${p.name} (${p.code})` : p.name }))
  const isRunning = activeTimer?.status === "running"
  const isPaused = activeTimer?.status === "paused"

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <Clock className="h-5 w-5 text-muted-foreground" />
        <CardTitle>Timer</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 py-6">
        {error ? <div className="w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}

        {!isRunning && !isPaused ? (
          <div className="flex w-full max-w-md flex-col gap-3">
            <SelectInput
              label="Project"
              value={selectedProject}
              onChange={setSelectedProject}
              options={projectOptions}
            />
            <TextInput
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="What are you working on?"
            />
          </div>
        ) : null}

        <div className="font-mono text-4xl font-semibold tabular-nums text-foreground/90">
          {formatDuration(elapsed)}
        </div>

        <div className="flex gap-2">
          {!isRunning && !isPaused ? (
            <Button type="button" onClick={startTimer} disabled={!selectedProject}>
              Start
            </Button>
          ) : null}
          {isRunning ? (
            <Button type="button" variant="secondary" onClick={pauseTimer}>
              Pause
            </Button>
          ) : null}
          {isPaused ? (
            <Button type="button" onClick={resumeTimer}>
              Resume
            </Button>
          ) : null}
          {(isRunning || isPaused) ? (
            <>
              <Button type="button" variant="destructive" onClick={stopTimer}>
                Stop & Save
              </Button>
            </>
          ) : null}
        </div>

        {activeTimer ? (
          <p className="text-xs text-muted-foreground">
            Project: {activeTimer.time_project?.name || "—"} · {isRunning ? "Running" : isPaused ? "Paused" : "Stopped"}
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function TimeTrackingPage(props) {
  const {
    active_slug,
    section,
    section_title,
    user_name,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)

  const key = section || "projects"

  const phaseCopy = useMemo(
    () => ({
      projects:
        "Phase 1 (Zoho-style): projects first, then log time on the Timesheets tab. Approvals and billing rules come later.",
      timesheets: "Phase 1: entry list + filters. Phase 2: week grid, submit, and manager approval like Zoho Timesheets.",
      timer: "Persistent stopwatch — start, pause, resume, and save to timesheet entries.",
    }),
    []
  )

  return (
    <BookShell activeSlug={active_slug || "time-tracking"} userName={user_name} access={access}>
      <div className="flex flex-col gap-6">
        <PageHeader
          context="Time Tracking"
          title={section_title}
          description={phaseCopy[key] || phaseCopy.projects}
        />

        {key === "projects" ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <FolderKanban className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="text-sm">Project directory</span>
          </div>
        ) : null}
        {key === "projects" ? <ProjectsPanel /> : null}

        {key === "timesheets" ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Table className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="text-sm">Timesheet entries</span>
          </div>
        ) : null}
        {key === "timesheets" ? <TimesheetsPanel /> : null}

        {key === "timer" ? <TimerPanel /> : null}
      </div>
    </BookShell>
  )
}
